import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Award, Users, GraduationCap, Heart, Mail, Phone, ArrowRight } from "lucide-react";

const STATS = [
    { value: "16+", label: "Years of Excellence" },
    { value: "1,500+", label: "Graduates Empowered" },
    { value: "90%+", label: "Workforce Placement" },
    { value: "500k+", label: "Scholarship Given" },
];

const PILLARS = [
    {
        icon: Users,
        title: "International Students",
        body: "HCC welcomes learners from across the globe into a supportive, multicultural community.",
    },
    {
        icon: GraduationCap,
        title: "Future-Ready Programs",
        body: "Certificate, Diploma, and Red Seal pathways aligned with Canadian workforce demand.",
    },
    {
        icon: Heart,
        title: "Partner Institutions",
        body: "Earn a Canadian credential through our distance learning and pathway partners.",
    },
];

export default function About() {
    return (
        <div className="bg-background" data-testid="about-page">
            {/* Hero */}
            <section className="max-w-6xl mx-auto px-6 pt-16 pb-10">
                <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3">
                    About Us
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05]">
                    Welcome to Heritage
                    <br />
                    Community College
                </h1>
                <p className="mt-6 text-lg text-foreground/75 max-w-2xl leading-relaxed">
                    At Heritage Community College, our Vision and Mission serve as the foundation
                    of everything we do. They reflect our unwavering commitment to academic
                    excellence, personal growth, and community impact — empowering students with
                    the knowledge, skills, and values needed to thrive in a dynamic and diverse
                    world.
                </p>
            </section>

            {/* Vision + Mission */}
            <section className="max-w-6xl mx-auto px-6 py-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="border-border">
                        <CardContent className="p-8">
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">
                                Our Vision
                            </div>
                            <h2 className="font-display text-2xl text-primary mb-3">
                                Lifelong learners, lifelong impact
                            </h2>
                            <p className="text-foreground/80 leading-relaxed">
                                Heritage Community College empowers lifelong learners through
                                knowledge, innovation, and integrity — fostering critical thinking,
                                personal growth, and a passion for learning. We envision a future
                                where every learner leads with insight, compassion, and impact,
                                creating positive change wherever they go.
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="border-border">
                        <CardContent className="p-8">
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">
                                Our Mission
                            </div>
                            <h2 className="font-display text-2xl text-primary mb-3 italic">
                                “Vestri Posterus Satus Hic”
                            </h2>
                            <p className="text-foreground/80 leading-relaxed">
                                Heritage Community College inspires students to grow intellectually
                                and personally through a supportive, multicultural environment. We
                                encourage curiosity, resilience, and responsibility — equipping
                                learners to thrive as capable, confident global citizens.
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Stats band */}
            <section className="bg-primary text-primary-foreground py-14 my-10">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2 text-center">
                        Heritage By The Numbers
                    </div>
                    <h2 className="font-display text-3xl text-primary-foreground text-center mb-10">
                        A community built on outcomes
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="about-stats">
                        {STATS.map((s) => (
                            <div key={s.label} className="text-center">
                                <div className="font-display text-4xl sm:text-5xl text-accent">
                                    {s.value}
                                </div>
                                <div className="text-sm text-primary-foreground/80 mt-2">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pillars */}
            <section className="max-w-6xl mx-auto px-6 py-10">
                <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3">
                    What we stand for
                </div>
                <h2 className="font-display text-3xl text-primary mb-8">
                    An education grounded in community
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {PILLARS.map(({ icon: Icon, title, body }) => (
                        <Card key={title} className="border-border">
                            <CardContent className="p-6">
                                <div className="h-10 w-10 rounded-full bg-accent/15 text-accent flex items-center justify-center mb-4">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-display text-lg text-primary mb-2">{title}</h3>
                                <p className="text-sm text-foreground/75 leading-relaxed">{body}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-6xl mx-auto px-6 py-16">
                <div className="rounded-sm border border-border bg-secondary/30 p-10 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                        <Award className="h-8 w-8 text-accent mb-3" />
                        <h3 className="font-display text-2xl text-primary">
                            Ready to begin your learning journey?
                        </h3>
                        <p className="text-foreground/75 mt-2">
                            Browse our online catalog or reach out — we're happy to help you choose
                            the right pathway.
                        </p>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                        <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                            <Link to="/" data-testid="about-cta-catalog">
                                Browse catalog
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Link>
                        </Button>
                        <Button asChild size="lg" variant="outline">
                            <Link to="/contact" data-testid="about-cta-contact">
                                Contact us
                            </Link>
                        </Button>
                    </div>
                </div>
                <div className="mt-6 flex items-center justify-center gap-6 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-2">
                        <Phone className="h-4 w-4" /> +1 (604) 593-5400
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Mail className="h-4 w-4" /> info@hccbconline.com
                    </span>
                </div>
            </section>
        </div>
    );
}
